// Contact.jsx
import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLocalContext } from "../../context/LocalContext";
import { resolveSiteEmail, resolveSitePhone } from "../../utils/siteConfig";
import "./Contact.css";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65 } },
};

const fade = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.65 } },
};

const Contact = () => {
  const { webinfo = {} } = useLocalContext();

  const phoneCall = webinfo?.phonecall || "";
  const phoneText = resolveSitePhone(webinfo) || webinfo?.phonecall || "Call us";
  const email = resolveSiteEmail(webinfo);
  const address = webinfo?.address || "Remote · Worldwide";
  const city = webinfo?.addressCity || "India";

  const mapSrc = useMemo(() => {
    const q = encodeURIComponent(city);
    return `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d241317.11609915975!2d72.74109703785945!3d19.082197839145994!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b63a9d90e14b%3A0x838fe179b1bdc674!2s${q}!5e0!3m2!1sen!2sin!4v1719999999999`;
  }, [city]);

  // simple UX state (optional)
  const [loading, setLoading] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    // ✅ Replace with your real submission (EmailJS / API / WhatsApp / Google Sheets)
    // For now: simulate success
    setTimeout(() => {
      setLoading(false);
      alert("Thanks! Your message has been received. We’ll reply within 24 hours.");
      e.target.reset();
    }, 600);
  };

  return (
    <section className="contact" aria-label="Contact">
      <div className="contact__wrap">
        {/* Header */}
        <motion.div
          className="contact__head"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.45 }}
        >
          <div className="contact__tag">
            <span className="dot" aria-hidden="true" />
            Contact
          </div>

          <h1 className="contact__title">
            Let’s plan your next <span>growth sprint</span>.
          </h1>

          <p className="contact__sub">
            Share your website and goal (leads, sales, bookings). We’ll respond
            with a quick audit + a clear next-step plan.
          </p>
        </motion.div>

        {/* Main Grid */}
        <div className="contact__grid">
          {/* Info */}
          <motion.aside
            className="contact__card contact__card--soft"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2 className="contact__h2">Reach us directly</h2>
            <p className="contact__p">
              Prefer quick coordination? Use the options below. We usually reply
              within <b>24 hours</b> (often faster).
            </p>

            <ul className="contact__chips" aria-label="Highlights">
              <li>Fast responses</li>
              <li>Clear timelines</li>
              <li>Transparent pricing</li>
              <li>Remote-friendly</li>
            </ul>

            <ul className="contact__list" aria-label="Contact details">
              <li className="contact__item">
                <i className="contact__icon" aria-hidden="true">
                  📞
                </i>
                <div>
                  <p className="contact__label">Phone</p>
                  <p className="contact__value">
                    <a href={phoneCall ? `tel:${phoneCall}` : "#"}>{phoneText}</a>
                  </p>
                </div>
              </li>

              <li className="contact__item">
                <i className="contact__icon" aria-hidden="true">
                  ✉️
                </i>
                <div>
                  <p className="contact__label">Email</p>
                  <p className="contact__value">
                    <a href={`mailto:${email}`}>{email}</a>
                  </p>
                </div>
              </li>

              <li className="contact__item">
                <i className="contact__icon" aria-hidden="true">
                  📍
                </i>
                <div>
                  <p className="contact__label">Location</p>
                  <p className="contact__value">{address}</p>
                </div>
              </li>

              <li className="contact__item">
                <i className="contact__icon" aria-hidden="true">
                  ⏱
                </i>
                <div>
                  <p className="contact__label">Hours</p>
                  <p className="contact__value">Mon–Sat · 10:00 AM – 7:00 PM IST</p>
                </div>
              </li>
            </ul>
          </motion.aside>

          {/* Form */}
          <motion.div
            className="contact__card"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2 className="contact__h2">Send a project brief</h2>
            <p className="contact__p">
              Tell us what you’re building and what success looks like. The
              more context you share, the faster we can recommend the best plan.
            </p>

            <form className="contact__form" onSubmit={onSubmit}>
              <div className="contact__row">
                <div className="contact__field">
                  <label>Your name</label>
                  <input className="contact__input" type="text" name="name" placeholder="Akash / John" required />
                </div>

                <div className="contact__field">
                  <label>Email</label>
                  <input className="contact__input" type="email" name="email" placeholder="you@company.com" required />
                </div>
              </div>

              <div className="contact__row">
                <div className="contact__field">
                  <label>Service</label>
                  <select className="contact__select" name="service" defaultValue="Website build">
                    <option>Website build</option>
                    <option>Landing page (CRO)</option>
                    <option>WordPress / Shopify</option>
                    <option>SEO & performance</option>
                    <option>Product UI/UX</option>
                    <option>Consultation</option>
                  </select>
                </div>

                <div className="contact__field">
                  <label>Budget range</label>
                  <select className="contact__select" name="budget" defaultValue="Not sure yet">
                    <option>Not sure yet</option>
                    <option>₹10k – ₹25k</option>
                    <option>₹25k – ₹60k</option>
                    <option>₹60k – ₹1.2L</option>
                    <option>₹1.2L+</option>
                  </select>
                </div>
              </div>

              <div className="contact__field">
                <label>Website / link (optional)</label>
                <input className="contact__input" type="url" name="website" placeholder="https://example.com" />
              </div>

              <div className="contact__field">
                <label>Project details</label>
                <textarea
                  className="contact__textarea"
                  name="message"
                  placeholder="What are you building? What’s the goal? Any deadline?"
                  required
                />
              </div>

              <div className="contact__actions">
                <button className="contact__btn contact__btn--primary" type="submit" disabled={loading}>
                  {loading ? "Sending..." : "Send Message"} <span aria-hidden="true">→</span>
                </button>

                <a className="contact__btn contact__btn--ghost" href={`mailto:${email}`}>
                  Email Instead
                </a>

                <p className="contact__fineprint">
                  By submitting, you agree to be contacted back regarding your inquiry.
                </p>
              </div>
            </form>
          </motion.div>
        </div>

        {/* Bottom: Map + FAQ */}
        <div className="contact__bottom">
          <motion.div
            className="contact__map"
            variants={fade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
          >
            <iframe title="Map" src={mapSrc} loading="lazy" allowFullScreen />
          </motion.div>

          <motion.div
            className="contact__faq"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
          >
            <div className="contact__qa">
              <h3>How fast do you respond?</h3>
              <p>Usually within 24 hours. For urgent requests, include “URGENT” in the subject.</p>
            </div>

            <div className="contact__qa">
              <h3>Do you work with international clients?</h3>
              <p>Yes — we’re remote-first. We coordinate via WhatsApp/Slack + weekly updates.</p>
            </div>

            <div className="contact__qa">
              <h3>What happens after I submit?</h3>
              <p>We’ll review your brief, ask 2–3 clarifying questions, then share a clear plan + estimate.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;